<template>
  <div>
    <PreLoader v-if="applicationIsLoading" />
    <div v-else-if="application">
      <div class="Inquiry-item">
        <div class="Inquiry-item__aside hide-under-delta">
          <div v-if="isApplicant" class="Inquiry-item__aside__logo">
            <img
              src="/images/kompetensforetagen.png"
              alt="Kompetensföretagen"
            />
          </div>
          <ApplicationStatus v-if="!isApplicant" />
          <ApplicationTypeSelector v-if="!isApplicant" />
          <ApplicationNotebook
            v-if="!isApplicant"
            :note="application.note"
            :id="application.id"
          />
          <ApplicationCompanyInfo
            :title="isApplicant ? 'Företagsinformation' : 'Om företaget'"
            :company="application"
          />
          <ApplicationButtons v-if="!isApplicant" />
          <div v-if="hasCertificate" class="Applicant__download-button">
            <div class="Applicant__download-button__title">Certifikat</div>
            <button
              type="button"
              :disabled="downloadIsLoading"
              :class="{
                'Button--approve-inactive Button--icon-download-inactive': downloadIsLoading
              }"
              class="Button Button--approve Button--block Button--large Button--icon Button--icon-download"
              @click="downloadCertificates"
            >
              Ladda ned certifikat och logotyper<br />
              ({{ application.certificates.validFrom }} -
              {{ application.certificates.validThrough }})
            </button>
            <a
              class="link"
              href="https://www.kompetensforetagen.se/medlemskap/etiska-regler/"
              target="_blank"
              >Etiska regler</a
            >
            <PreLoader v-if="downloadIsLoading" />
          </div>
        </div>
        <div class="Inquiry-item__content">
          <ApplicationIntro :heading="getHeading" :lead="getLeadIntro" />
          <div v-if="!isApplicant" class="Application__download-button">
            <button
              type="button"
              :disabled="downloadIsLoading"
              class="Button Button--primary Button--block Button--icon Button--icon-download"
              :class="{
                'Button--primary-inactive Button--icon-download-inactive': downloadIsLoading
              }"
              @click="downloadEverything"
            >
              Ladda ner alla inskickade handlingar
            </button>
            <PreLoader v-if="downloadIsLoading" />
          </div>
          <ApplicationActionList
            :disabled="submitted"
            actionType="ATTACHMENT"
            :actions="attachmentActions"
            component="ApplicationActionAttachment"
            add-new-text="Lägg till bilaga"
            add-new-label="Välj bilaga att lägga till"
          />
          <div v-if="isApplicant && !decided" class="Application__info">
            {{ infoText }}
          </div>
          <div
            v-if="
              isApplicant && !decided && !(notDoneActions.length || submitted)
            "
            class="Application__approve-button"
          >
            <button
              class="Button Button--block Button--large"
              :class="{
                'Button--approve-inactive': notDoneActions.length || submitted,
                'Button--approve': !notDoneActions.length
              }"
              @click="submit"
            >
              Skicka in ansökan
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";

import _ from "lodash";
import PreLoader from "@/components/PreLoader";
import ApplicationIntro from "./ApplicationIntro";
import ApplicationCompanyInfo from "./ApplicationCompanyInfo";
import ApplicationActionList from "./ApplicationActionList";
import ApplicationNotebook from "./ApplicationNotebook";
import ApplicationButtons from "./ApplicationButtons";
import ApplicationTypeSelector from "./ApplicationTypeSelector";
import ApplicationStatus from "./ApplicationStatus";
import { authorizationTypes, applicationStatuses } from "@/utils/constants";
import { saveAs } from "file-saver";

export default {
  name: "Application",
  components: {
    PreLoader,
    ApplicationCompanyInfo,
    ApplicationIntro,
    ApplicationNotebook,
    ApplicationActionList,
    ApplicationButtons,
    ApplicationTypeSelector,
    ApplicationStatus
  },
  props: {
    isApplicant: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      downloadIsLoading: false
    };
  },
  computed: {
    ...mapState("applicationStore", ["application"]),
    ...mapGetters("applicationStore", [
      "attachmentActions",
      "applicationIsLoading",
      "notDoneActions"
    ]),
    getTypes() {
      return _(Object.keys(authorizationTypes))
        .filter(type => this.application[authorizationTypes[type].value])
        .map(type => authorizationTypes[type].text)
        .value()
        .sort()
        .join(", ");
    },
    getHeading() {
      return this.isApplicant
        ? `Ansökan gäller: ${this.getTypes}`
        : this.application.name;
    },
    getLeadIntro() {
      return `
        <p>Nedan följer de dokument som behöver biläggas för att gå vidare i auktorisationsprocessen. För varje dokument finns en funktion för att ladda upp en pdf-fil och en chattfunktion för kommunikation med våra handläggare.</p>
        <p>När alla dokument är bilagda och ansökan insänd, påbörjas granskningsprocessen. Då låses processen och ni kan inte redigera i er ansökan.</p>
        <p><a class='link' href='https://www.kompetensforetagen.se/auktorisation-2/ansok-om-auktorisation/steg-1-villkor-for-auktorisation-kan-foretaget-bli-auktoriserat' target='_blank'>Se villkor för auktorisation.</a></p>`;
    },
    infoText() {
      if (this.notDoneActions.length) {
        return "Du måste göra klart alla delmoment ovan innan ansökan kan skickas in";
      } else if (
        !this.notDoneActions.length &&
        this.application.status === applicationStatuses.SUBMITTED.value
      ) {
        return "Ansökan är inskickad och inväntar granskning!";
      } else {
        return "Nu är alla delmoment klara och ansökan kan skickas in";
      }
    },
    submitted() {
      return this.application.status === applicationStatuses.SUBMITTED.value;
    },
    decided() {
      return this.application.status === applicationStatuses.DECIDED.value;
    },
    hasCertificate() {
      return this.application.certificates.exists === true;
    }
  },
  created() {
    if (this.isApplicant) {
      this.getApplication({ id: this.$route.params.id });
    } else {
      if (
        !this.application ||
        this.application.id !== parseInt(this.$route.params.id, 10)
      ) {
        this.getApplication({ id: this.$route.params.id });
      }
    }
  },

  watch: {
    "application.id": function(id) {
      const downloadUrl =
        this.$route.params.pathMatch === "downloadcertificate";
      if (id && downloadUrl && this.hasCertificate) {
        this.downloadCertificates(id);
      }
    }
  },

  methods: {
    ...mapActions("applicationStore", [
      "getApplication",
      "submitApplication",
      "getZippedAttachments",
      "getZippedCertificates"
    ]),
    ...mapActions("messageStore", ["showError"]),
    submit() {
      if (this.notDoneActions.length) {
        this.showError({
          text:
            "Alla delmoment är inte klara. Kontrollera att du fyllt i alla uppgifter och laddat upp alla bilagor."
        });
      } else {
        this.submitApplication();
      }
    },
    downloadEverything() {
      this.downloadIsLoading = true;
      this.getZippedAttachments({
        id: this.application.id
      }).then(response => {
        this.downloadIsLoading = false;
        let blob = new Blob([response], { type: "application/zip" });
        const fileName = this.application.orgNo;
        saveAs(blob, `${fileName}.zip`);
      });
    },
    downloadCertificates(id) {
      this.downloadIsLoading = true;
      this.getZippedCertificates({
        id: this.application.id
      }).then(response => {
        this.downloadIsLoading = false;
        let blob = new Blob([response], { type: "application/zip" });
        saveAs(blob, "kompetensforetagen_auktorisation.zip");
      });
    }
  }
};
</script>
<style lang="scss" scoped>
.Inquiry-item {
  display: flex;
  justify-content: stretch;
  min-height: 100vh;
  // padding-top: $header-height;
  // margin-top: -$header-height;

  &__aside {
    @extend %text-S;
    background-color: $color-white-two;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    flex: none;
    padding: $spacing-L * 2 $spacing-L $spacing-L $spacing-L;
    width: 300px;
    &__logo {
      position: relative;
      width: 100%;
      img {
        width: 100%;
      }
    }
  }

  &__content {
    flex: auto;
    padding: $spacing-L * 2 0 $spacing-L 0;

    @include media-from($beta-width) {
      padding-left: $spacing-L;
      padding-bottom: $spacing-L;
    }
  }

  &__header {
    @extend %text-S;
    margin-bottom: $spacing;
  }

  &__approve-button {
    padding-right: $spacing-L * 2;
  }
}

.Application__approve-button,
.Application__download-button,
.Application__info {
  margin-right: $spacing-L;
  margin-bottom: $spacing;
  @include media-from($beta-width) {
    margin-right: $spacing-L * 2;
  }
}
.Applicant__download-button {
  &__title {
    @extend %text-XS;
    @extend %text-subtle;
  }
  .Button--icon-download {
    height: $spacing-L * 2.5;
    margin: $spacing-S 0 $spacing-S * 2;
    padding-left: $spacing-L * 1.6;
    padding-right: $spacing-S * 2;
    &::before {
      left: $spacing-S * 2;
    }
  }
  .Button--icon-download-inactive {
    &::before {
      opacity: 0.5;
    }
  }
}
.Button--primary-inactive {
  opacity: 0.5;
}
.Application__info {
  font-weight: 700;
  padding: $spacing;
  background: $color-pale-grey;
  border: $border-default;
  border-radius: $border-radius-default;
  text-align: center;
}
</style>
